import React, { useState, useEffect, useRef, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Layout,
  Package,
  Users,
  HelpCircle,
  User,
  Menu,
  X,
  FlaskConical,
} from "lucide-react";
import "./sidebar.css";
import { GlobalContext } from "../../GlobalContext";

const Sidebar = () => {
  const { userData, isLoading, subscribe, navigationHistory } =
    useContext(GlobalContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    setIsSidebarOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const unsubscribe = subscribe((eventType) => {
      if (eventType === "USER_DATA_UPDATED") {
        // No need to refetch; GlobalContext updates userData
      }
    });
    return unsubscribe;
  }, [subscribe]);

  const currentPath = location.pathname.replace(/\/+$/, "");
  const isPatient = userData.role === "patient";
  const isNotVerified = userData.verificationStatus !== "Verified";
  const restrictAccess = !isPatient && isNotVerified;

  const getActiveClass = (path) => {
    const sidebarPaths = [
      "/dashboard",
      "/order-a-test",
      "/my-orders",
      "/manage-patients",
      "/help-and-support",
      "/profile",
    ];

    const pageConfig = {
      "/add-patient": { parent: "/manage-patients" },
      "/medical-intake-form": { parent: "/dashboard" },
      "/review-test-order": { parent: "/order-a-test" },
      "/talk-to-specialist": { parent: "/order-a-test" },
      "/reset-password": { parent: "/profile" },
      "/contact-form": { parent: "/help-and-support" },
      "/order-test": { parent: "/order-a-test" },
      "/partner-labs": { parent: "/order-a-test" },
    };

    if (currentPath === "/resources") {
      return "";
    }

    if (currentPath === "/verification-status") {
      return path === "/dashboard" ? "active" : "";
    }

    if (currentPath === "/review-test-order") {
      const queryParams = new URLSearchParams(location.search);
      const fromOrderDetails = queryParams.get("fromOrderDetails") === "1";
      if (fromOrderDetails) {
        return path === "/my-orders" ? "active" : "";
      }
      return path === "/order-a-test" ? "active" : "";
    }

    if (sidebarPaths.includes(currentPath)) {
      return currentPath === path ? "active" : "";
    }

    if (currentPath.match(/^\/my-order\/([^\/]+)/)) {
      const prevPage = navigationHistory.slice(-2, -1)[0];
      if (prevPage && prevPage.path && prevPage.path.match(/^\/patient-details\/([^\/]+)/)) {
        return path === "/manage-patients" ? "active" : "";
      }
      return path === "/my-orders" ? "active" : "";
    }

    if (currentPath.match(/^\/patient-details\/([^\/]+)/)) {
      return path === "/manage-patients" ? "active" : "";
    }

    if (currentPath.match(/^\/medical-test\/([^\/]+)/)) {
      return path === "/order-a-test" ? "active" : "";
    }

    if (currentPath === "/contact-support") {
      const prevPage = navigationHistory.slice(-2, -1)[0];
      if (prevPage && prevPage.path && prevPage.path.match(/^\/my-order\/([^\/]+)/)) {
        const prevPrevPage = navigationHistory.slice(-3, -2)[0];
        if (prevPrevPage && prevPrevPage.path && prevPrevPage.path.match(/^\/patient-details\/([^\/]+)/)) {
          return path === "/manage-patients" ? "active" : "";
        }
        return path === "/my-orders" ? "active" : "";
      }
      return path === "/my-orders" ? "active" : "";
    }

    if (currentPath === "/contact-form") {
      const prevPage = navigationHistory.slice(-2, -1)[0];
      if (prevPage && prevPage.path === "/verification-status") {
        return path === "/dashboard" ? "active" : "";
      }
    }

    if (pageConfig[currentPath]) {
      let parentPath = pageConfig[currentPath].parent;

      if (currentPath === "/order-test") {
        const prevPage = navigationHistory.slice(-2, -1)[0];
        if (prevPage && prevPage.path === "/") {
          parentPath = "/order-a-test";
        }
      } else {
        const prevPage = navigationHistory.slice(-2, -1)[0];
        if (
          prevPage &&
          prevPage.fullPath !== currentPath &&
          prevPage.fullPath !== parentPath
        ) {
          const prevPath = prevPage.path;
          if (sidebarPaths.includes(prevPath)) {
            parentPath = prevPath;
          }
        }
      }

      return parentPath === path ? "active" : "";
    }

    return "";
  };

  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isSidebarOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setIsSidebarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen]);

  const containerVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -10 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.02, transition: { duration: 0.2 } },
    tap: { scale: 0.98 },
  };

  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const profileVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { type: "spring", stiffness: 300, damping: 20 },
    },
  };

  const handleDashboardNavigation = () => {
    if (restrictAccess) {
      navigate("/verification-status");
    } else {
      navigate("/dashboard");
    }
  };

  const handleLogoClick = () => {
    window.location.href = "/";
  };

  if (isMobile) {
    return (
      <>
        <motion.div
          className="mobile-menu-toggle"
          onClick={toggleSidebar}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
        >
          <Menu size={24} color="#FFFFFF" />
        </motion.div>
        <AnimatePresence>
          {isSidebarOpen && (
            <motion.div
              ref={sidebarRef}
              className={`sidebar-mobile ${isSidebarOpen ? "open" : ""}`}
              initial={{ x: "-70%" }}
              animate={{ x: 0 }}
              exit={{ x: "-70%" }}
              transition={{ type: "tween", duration: 0.3 }}
            >
              <motion.div
                className="sidebar-mobile-header"
                onClick={toggleSidebar}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X size={24} color="#3A3F6A" />
              </motion.div>
              {/* <motion.div className="sidebar-logo" variants={logoVariants}>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
                  alt="Direct by Syndicate Bio"
                  onClick={handleLogoClick}
                />
              </motion.div> */}
              <motion.div
                className="sidebar-top-pages"
                variants={containerVariants}
              >
                <motion.button
                  className={`sidebar-button ${getActiveClass("/dashboard")}`}
                  onClick={() => {
                    handleDashboardNavigation();
                    toggleSidebar();
                  }}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <svg
                    className={`sidebar-icon ${getActiveClass("/dashboard")}`}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 2V22"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M2 9.5H12"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 14.5H22"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Dashboard
                </motion.button>
                <motion.button
                  className={`sidebar-button ${getActiveClass(
                    "/order-a-test"
                  )} ${restrictAccess ? "disabled" : ""}`}
                  onClick={() => {
                    if (!restrictAccess) {
                      navigate("/order-a-test");
                      toggleSidebar();
                    }
                  }}
                  disabled={restrictAccess}
                  variants={buttonVariants}
                  whileHover={restrictAccess ? {} : "hover"}
                  whileTap={restrictAccess ? {} : "tap"}
                >
                  <svg
                    className={`sidebar-icon ${getActiveClass(
                      "/order-a-test"
                    )}`}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.15882 22C3.97882 22 3.13882 19.47 4.49882 16.39L8.74882 6.74H8.44882C7.79882 6.74 7.19882 6.48 6.76882 6.05C6.32882 5.62 6.06882 5.02 6.06882 4.37C6.06882 3.07 7.12882 2 8.43882 2H15.5488C16.2088 2 16.7988 2.27 17.2288 2.7C17.7888 3.26 18.0688 4.08 17.8588 4.95C17.5888 6.03 16.5488 6.74 15.4388 6.74H15.2788L19.4988 16.4C20.8488 19.48 19.9688 22 15.8288 22H8.15882Z"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5.9375 13.1194C5.9375 13.1194 8.9975 12.9994 11.9975 13.9994C14.9975 14.9994 17.8275 13.1094 17.8275 13.1094"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Order a test
                </motion.button>
                <motion.button
                  className={`sidebar-button ${getActiveClass("/my-orders")} ${
                    restrictAccess ? "disabled" : ""
                  }`}
                  onClick={() => {
                    if (!restrictAccess) {
                      navigate("/my-orders");
                      toggleSidebar();
                    }
                  }}
                  disabled={restrictAccess}
                  variants={buttonVariants}
                  whileHover={restrictAccess ? {} : "hover"}
                  whileTap={restrictAccess ? {} : "tap"}
                >
                  <svg
                    className={`sidebar-icon ${getActiveClass("/my-orders")}`}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M21.6602 10.4385L20.6802 14.6185C19.8402 18.2285 18.1802 19.6885 15.0602 19.3885C14.5602 19.3485 14.0202 19.2585 13.4402 19.1185L11.7602 18.7185C7.59018 17.7285 6.30018 15.6685 7.28018 11.4885L8.26018 7.29854C8.46018 6.44854 8.70018 5.70854 9.00018 5.09854C10.1702 2.67854 12.1602 2.02854 15.5002 2.81854L17.1702 3.20854C21.3602 4.18854 22.6402 6.25854 21.6602 10.4385Z"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M15.0584 19.3916C14.4384 19.8116 13.6584 20.1616 12.7084 20.4716L11.1284 20.9916C7.15839 22.2716 5.06839 21.2016 3.77839 17.2316L2.49839 13.2816C1.21839 9.31156 2.27839 7.21156 6.24839 5.93156L7.82839 5.41156C8.23839 5.28156 8.62839 5.17156 8.99839 5.10156C8.69839 5.71156 8.45839 6.45156 8.25839 7.30156L7.27839 11.4916C6.29839 15.6716 7.58839 17.7316 11.7584 18.7216L13.4384 19.1216C14.0184 19.2616 14.5584 19.3516 15.0584 19.3916Z"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12.6406 8.53125L17.4906 9.76125"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M11.6562 12.3984L14.5563 13.1384"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  My orders
                </motion.button>
                {userData.role === "provider" && (
                  <motion.button
                    className={`sidebar-button ${getActiveClass(
                      "/manage-patients"
                    )} ${restrictAccess ? "disabled" : ""}`}
                    onClick={() => {
                      if (!restrictAccess) {
                        navigate("/manage-patients");
                        toggleSidebar();
                      }
                    }}
                    disabled={restrictAccess}
                    variants={buttonVariants}
                    whileHover={restrictAccess ? {} : "hover"}
                    whileTap={restrictAccess ? {} : "tap"}
                  >
                    <svg
                      className={`sidebar-icon ${getActiveClass(
                        "/manage-patients"
                      )}`}
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.1625 10.87C9.0625 10.86 8.9425 10.86 8.8325 10.87C6.4525 10.79 4.5625 8.84 4.5625 6.44C4.5625 3.99 6.5425 2 9.0025 2C11.4525 2 13.4425 3.99 13.4425 6.44C13.4325 8.84 11.5425 10.79 9.1625 10.87Z"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M16.4112 4C18.3512 4 19.9112 5.57 19.9112 7.5C19.9112 9.39 18.4113 10.93 16.5413 11C16.4613 10.99 16.3713 10.99 16.2812 11"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M4.15875 14.56C1.73875 16.18 1.73875 18.82 4.15875 20.43C6.90875 22.27 11.4188 22.27 14.1688 20.43C16.5888 18.81 16.5888 16.17 14.1688 14.56C11.4288 12.73 6.91875 12.73 4.15875 14.56Z"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M18.3438 20C19.0638 19.85 19.7437 19.56 20.3037 19.13C21.8637 17.96 21.8637 16.03 20.3037 14.86C19.7537 14.44 19.0838 14.16 18.3738 14"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    Manage patients
                  </motion.button>
                )}
              </motion.div>
              <motion.div
                className="sidebar-bottom-pages"
                variants={containerVariants}
              >
                <motion.button
                  className={`sidebar-button ${getActiveClass(
                    "/help-and-support"
                  )}`}
                  onClick={() => {
                    navigate("/help-and-support");
                    toggleSidebar();
                  }}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <svg
                    className={`sidebar-icon ${getActiveClass(
                      "/help-and-support"
                    )}`}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 18.4297H13L8.54999 21.3897C7.88999 21.8297 7 21.3598 7 20.5598V18.4297C4 18.4297 2 16.4297 2 13.4297V7.42969C2 4.42969 4 2.42969 7 2.42969H17C20 2.42969 22 4.42969 22 7.42969V13.4297C22 16.4297 20 18.4297 17 18.4297Z"
                      stroke-width="1.5"
                      stroke-miterlimit="10"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12.0037 11.3594V11.1494C12.0037 10.4694 12.4238 10.1094 12.8438 9.8194C13.2537 9.5394 13.6637 9.17941 13.6637 8.51941C13.6637 7.59941 12.9237 6.85938 12.0037 6.85938C11.0837 6.85938 10.3438 7.59941 10.3438 8.51941"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M11.9955 13.75H12.0045"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Help & support
                </motion.button>
                <motion.button
                  className={`sidebar-button ${getActiveClass("/profile")}`}
                  onClick={() => {
                    navigate("/profile");
                    toggleSidebar();
                  }}
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <svg
                    className={`sidebar-icon ${getActiveClass("/profile")}`}
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 9.10937V14.8794C3 16.9994 3 16.9994 5 18.3494L10.5 21.5294C11.33 22.0094 12.68 22.0094 13.5 21.5294L19 18.3494C21 16.9994 21 16.9994 21 14.8894V9.10937C21 6.99937 21 6.99937 19 5.64937L13.5 2.46937C12.68 1.98937 11.33 1.98937 10.5 2.46937L5 5.64937C3 6.99937 3 6.99937 3 9.10937Z"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Profile & settings
                </motion.button>
              </motion.div>
              <motion.div
                variants={itemVariants}
                className="sidebar-divider"
              ></motion.div>
              <motion.div
                className="sidebar-profile"
                variants={profileVariants}
              >
                <motion.div
                  className="sidebar-profile-pic"
                  whileHover={{ scale: 1.1 }}
                  transition={{ type: "spring", stiffness: 300, damping: 10 }}
                >
                  {isLoading ? (
                    "..."
                  ) : userData.profilePic ? (
                    <img
                      src={userData.profilePic}
                      alt="Profile"
                      className="sidebar-profile-img"
                      onError={() => {}}
                    />
                  ) : (
                    userData.firstName.charAt(0).toUpperCase()
                  )}
                </motion.div>
                <div className="sidebar-profile-info">
                  <div className="sidebar-profile-name">{`${userData.firstName} ${userData.lastName}`}</div>
                  <div className="sidebar-profile-email">{userData.email}</div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </>
    );
  }

  return (
    <motion.div
      className="sidebar-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="sidebar-logo" variants={logoVariants}>
        <a href="/">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
            alt="Direct by Syndicate Bio"
          />
        </a>
      </motion.div>
      <motion.div className="sidebar-top-pages" variants={containerVariants}>
        <motion.button
          className={`sidebar-button ${getActiveClass("/dashboard")}`}
          onClick={handleDashboardNavigation}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <svg
            className={`sidebar-icon ${getActiveClass("/dashboard")}`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9 22H15C20 22 22 20 22 15V9C22 4 20 2 15 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22Z"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 2V22"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M2 9.5H12"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 14.5H22"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Dashboard
        </motion.button>
        <motion.button
          className={`sidebar-button ${getActiveClass("/order-a-test")} ${
            restrictAccess ? "disabled" : ""
          }`}
          onClick={() => {
            if (!restrictAccess) navigate("/order-a-test");
          }}
          disabled={restrictAccess}
          variants={buttonVariants}
          whileHover={restrictAccess ? {} : "hover"}
          whileTap={restrictAccess ? {} : "tap"}
        >
          <svg
            className={`sidebar-icon ${getActiveClass("/order-a-test")}`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M8.15882 22C3.97882 22 3.13882 19.47 4.49882 16.39L8.74882 6.74H8.44882C7.79882 6.74 7.19882 6.48 6.76882 6.05C6.32882 5.62 6.06882 5.02 6.06882 4.37C6.06882 3.07 7.12882 2 8.43882 2H15.5488C16.2088 2 16.7988 2.27 17.2288 2.7C17.7888 3.26 18.0688 4.08 17.8588 4.95C17.5888 6.03 16.5488 6.74 15.4388 6.74H15.2788L19.4988 16.4C20.8488 19.48 19.9688 22 15.8288 22H8.15882Z"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M5.9375 13.1194C5.9375 13.1194 8.9975 12.9994 11.9975 13.9994C14.9975 14.9994 17.8275 13.1094 17.8275 13.1094"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Order a test
        </motion.button>
        <motion.button
          className={`sidebar-button ${getActiveClass("/my-orders")} ${
            restrictAccess ? "disabled" : ""
          }`}
          onClick={() => {
            if (!restrictAccess) navigate("/my-orders");
          }}
          disabled={restrictAccess}
          variants={buttonVariants}
          whileHover={restrictAccess ? {} : "hover"}
          whileTap={restrictAccess ? {} : "tap"}
        >
          <svg
            className={`sidebar-icon ${getActiveClass("/my-orders")}`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M21.6602 10.4385L20.6802 14.6185C19.8402 18.2285 18.1802 19.6885 15.0602 19.3885C14.5602 19.3485 14.0202 19.2585 13.4402 19.1185L11.7602 18.7185C7.59018 17.7285 6.30018 15.6685 7.28018 11.4885L8.26018 7.29854C8.46018 6.44854 8.70018 5.70854 9.00018 5.09854C10.1702 2.67854 12.1602 2.02854 15.5002 2.81854L17.1702 3.20854C21.3602 4.18854 22.6402 6.25854 21.6602 10.4385Z"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M15.0584 19.3916C14.4384 19.8116 13.6584 20.1616 12.7084 20.4716L11.1284 20.9916C7.15839 22.2716 5.06839 21.2016 3.77839 17.2316L2.49839 13.2816C1.21839 9.31156 2.27839 7.21156 6.24839 5.93156L7.82839 5.41156C8.23839 5.28156 8.62839 5.17156 8.99839 5.10156C8.69839 5.71156 8.45839 6.45156 8.25839 7.30156L7.27839 11.4916C6.29839 15.6716 7.58839 17.7316 11.7584 18.7216L13.4384 19.1216C14.0184 19.2616 14.5584 19.3516 15.0584 19.3916Z"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12.6406 8.53125L17.4906 9.76125"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M11.6562 12.3984L14.5563 13.1384"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          My orders
        </motion.button>
        {userData.role === "provider" && (
          <motion.button
            className={`sidebar-button ${getActiveClass("/manage-patients")} ${
              restrictAccess ? "disabled" : ""
            }`}
            onClick={() => {
              if (!restrictAccess) navigate("/manage-patients");
            }}
            disabled={restrictAccess}
            variants={buttonVariants}
            whileHover={restrictAccess ? {} : "hover"}
            whileTap={restrictAccess ? {} : "tap"}
          >
            <svg
              className={`sidebar-icon ${getActiveClass("/manage-patients")}`}
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M9.1625 10.87C9.0625 10.86 8.9425 10.86 8.8325 10.87C6.4525 10.79 4.5625 8.84 4.5625 6.44C4.5625 3.99 6.5425 2 9.0025 2C11.4525 2 13.4425 3.99 13.4425 6.44C13.4325 8.84 11.5425 10.79 9.1625 10.87Z"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M16.4112 4C18.3512 4 19.9112 5.57 19.9112 7.5C19.9112 9.39 18.4113 10.93 16.5413 11C16.4613 10.99 16.3713 10.99 16.2812 11"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M4.15875 14.56C1.73875 16.18 1.73875 18.82 4.15875 20.43C6.90875 22.27 11.4188 22.27 14.1688 20.43C16.5888 18.81 16.5888 16.17 14.1688 14.56C11.4288 12.73 6.91875 12.73 4.15875 14.56Z"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M18.3438 20C19.0638 19.85 19.7437 19.56 20.3037 19.13C21.8637 17.96 21.8637 16.03 20.3037 14.86C19.7537 14.44 19.0838 14.16 18.3738 14"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Manage patients
          </motion.button>
        )}
      </motion.div>
      <motion.div className="sidebar-bottom-pages" variants={containerVariants}>
        <motion.button
          className={`sidebar-button ${getActiveClass("/help-and-support")}`}
          onClick={() => navigate("/help-and-support")}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <svg
            className={`sidebar-icon ${getActiveClass("/help-and-support")}`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M17 18.4297H13L8.54999 21.3897C7.88999 21.8297 7 21.3598 7 20.5598V18.4297C4 18.4297 2 16.4297 2 13.4297V7.42969C2 4.42969 4 2.42969 7 2.42969H17C20 2.42969 22 4.42969 22 7.42969V13.4297C22 16.4297 20 18.4297 17 18.4297Z"
              stroke-width="1.5"
              stroke-miterlimit="10"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12.0037 11.3594V11.1494C12.0037 10.4694 12.4238 10.1094 12.8438 9.8194C13.2537 9.5394 13.6637 9.17941 13.6637 8.51941C13.6637 7.59941 12.9237 6.85938 12.0037 6.85938C11.0837 6.85938 10.3438 7.59941 10.3438 8.51941"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M11.9955 13.75H12.0045"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Help & support
        </motion.button>
        <motion.button
          className={`sidebar-button ${getActiveClass("/profile")}`}
          onClick={() => navigate("/profile")}
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
        >
          <svg
            className={`sidebar-icon ${getActiveClass("/profile")}`}
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M3 9.10937V14.8794C3 16.9994 3 16.9994 5 18.3494L10.5 21.5294C11.33 22.0094 12.68 22.0094 13.5 21.5294L19 18.3494C21 16.9994 21 16.9994 21 14.8894V9.10937C21 6.99937 21 6.99937 19 5.64937L13.5 2.46937C12.68 1.98937 11.33 1.98937 10.5 2.46937L5 5.64937C3 6.99937 3 6.99937 3 9.10937Z"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
            <path
              d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          Profile & settings
        </motion.button>
      </motion.div>
      <motion.div
        variants={itemVariants}
        className="sidebar-divider"
      ></motion.div>
      <motion.div className="sidebar-profile" variants={profileVariants}>
        <motion.div
          className="sidebar-profile-pic"
          whileHover={{ scale: 1.1 }}
          transition={{ type: "spring", stiffness: 300, damping: 10 }}
        >
          {isLoading ? (
            "N"
          ) : userData.profilePic ? (
            <img
              src={userData.profilePic}
              alt="Profile"
              className="sidebar-profile-img"
              onError={() => {}}
            />
          ) : (
            userData.firstName.charAt(0).toUpperCase()
          )}
        </motion.div>
        <div className="sidebar-profile-info">
          <div className="sidebar-profile-name">{`${userData.firstName} ${userData.lastName}`}</div>
          <div className="sidebar-profile-email">{userData.email}</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default Sidebar;
